import React, { useState } from 'react';
import { useSessionStorage } from '@/hooks/useSessionStorage';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FilterOperator, Comparator } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Search, UserPlus, Trash2, Loader2, Mail, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUsers, useCreateUser } from '@/features/Users/hooks/useUsers';
import { Roles, ROLE_DISPLAY } from '@/features/Users/userTypes';
import {
  searchProjectMembers,
  postAddProjectMemberCustomer,
  removeProjectMember,
} from './SiteUsersApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// Form schema for inviting new users
const inviteUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.enum(['User', 'ProjectAdmin'], { required_error: 'A role is required' }),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

// Add User Modal Component
const AddUserModal = ({
  isOpen,
  onClose,
  onAddUser,
  existingUserIds = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (userId: string) => void;
  existingUserIds: string[];
}) => {
  const { toast } = useToast();

  // State for the modal
  const [activeTab, setActiveTab] = useSessionStorage('site-users-tab', 'existing');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  // State for form submission errors
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form for inviting new users
  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: 'User' as const,
    },
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  // Query for existing users
  const { data: usersData, isLoading } = useUsers({
    searchValue: searchTerm,
    pageSize: 10,
  });

  // Mutation for creating a new user
  const createUserMutation = useCreateUser();

  // Filter out users that are already added to the site
  const availableUsers =
    usersData?.rows?.filter((user) => !existingUserIds.includes(user.id || '')) || [];

  // Handle adding an existing user
  const handleAddExistingUser = () => {
    if (selectedUserId) {
      onAddUser(selectedUserId);
      setSelectedUserId(null);
      onClose();
    }
  };

  // Handle form submission
  const onSubmit = async (data: InviteUserFormData) => {
    try {
      setIsSubmitting(true);
      setFormError(null);

      const userData = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        roles: [data.role],
      };

      const result = await createUserMutation.mutateAsync(userData);

      if (result) {
        toast({
          title: 'User invited successfully',
          description: 'The user has been invited and added to the site.',
          variant: 'default',
        });

        onAddUser(result.user?.id || '');
        form.reset();
        onClose();
      }
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : 'Failed to invite user. Please try again.'
      );
      toast({
        title: 'Error inviting user',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border border-gray-800 sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-white">Add User to Site</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-none border-x-0 border-t-0 bg-[#1a1a1a]">
            <TabsTrigger
              value="existing"
              className="flex items-center gap-2 data-[state=active]:bg-[#333]"
            >
              <User className="h-4 w-4" />
              Existing User
            </TabsTrigger>
            <TabsTrigger
              value="new"
              className="flex items-center gap-2 data-[state=active]:bg-[#333]"
            >
              <Mail className="h-4 w-4" />
              Invite New User
            </TabsTrigger>
          </TabsList>

          <TabsContent value="existing" className="space-y-4 p-4">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
              <Input
                type="search"
                isSearch
                placeholder="Search users by name or email..."
                className="w-full bg-[#1a1a1a] pl-8 text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="max-h-[400px] overflow-y-auto rounded-md border">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-lime-500" />
                </div>
              ) : availableUsers.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`cursor-pointer p-3 hover:bg-gray-800/50 ${
                        selectedUserId === user.id ? 'bg-gray-800/70' : ''
                      }`}
                      onClick={() => setSelectedUserId(user.id || null)}
                    >
                      <div className="font-semibold text-white">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-400">{user.email}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-400">
                  {searchTerm ? 'No users found' : 'Start typing to search for users'}
                </div>
              )}
            </div>

            <div className="flex flex-col justify-end gap-2">
              <Button
                onClick={handleAddExistingUser}
                variant="default"
                disabled={!selectedUserId || createUserMutation.isPending}
              >
                {createUserMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Add User
              </Button>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="new" className="p-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="user@example.com"
                          className="bg-[#1a1a1a] text-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-medium">Role</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <label className="block w-full cursor-pointer">
                            <input
                              type="radio"
                              className="hidden"
                              checked={field.value === 'User'}
                              onChange={() => field.onChange('User')}
                            />
                            <div className="flex items-center space-x-3 rounded-md border p-4 transition-colors hover:bg-gray-800/50">
                              <div
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                  field.value === 'User'
                                    ? 'border-lime-500'
                                    : 'border-gray-400'
                                }`}
                              >
                                {field.value === 'User' && (
                                  <div className="h-2 w-2 rounded-full bg-lime-500" />
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold">{ROLE_DISPLAY[Roles.User].label}</h3>
                                <p className="text-sm text-gray-400">
                                  {ROLE_DISPLAY[Roles.User].description}
                                </p>
                              </div>
                            </div>
                          </label>
                          <label className="block w-full cursor-pointer">
                            <input
                              type="radio"
                              className="hidden"
                              checked={field.value === 'ProjectAdmin'}
                              onChange={() => field.onChange('ProjectAdmin')}
                            />
                            <div className="flex items-center space-x-3 rounded-md border p-4 transition-colors hover:bg-gray-800/50">
                              <div
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                  field.value === 'ProjectAdmin'
                                    ? 'border-lime-500'
                                    : 'border-gray-400'
                                }`}
                              >
                                {field.value === 'ProjectAdmin' && (
                                  <div className="h-2 w-2 rounded-full bg-lime-500" />
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold">{ROLE_DISPLAY[Roles.ProjectAdmin].label}</h3>
                                <p className="text-sm text-gray-400">
                                  {ROLE_DISPLAY[Roles.ProjectAdmin].description}
                                </p>
                              </div>
                            </div>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {formError && (
                  <div className="rounded-md bg-red-900/20 p-4 text-sm text-red-500">
                    {formError}
                  </div>
                )}
                <div className="flex flex-col justify-end gap-2">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending Invite...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

interface SiteUsersProps {
  siteId: string;
  companyId?: string;
}

export const SiteUsers: React.FC<SiteUsersProps> = ({ siteId, companyId: propCompanyId }) => {
  const { companyId: paramCompanyId } = useParams<{ companyId: string }>();
  const companyId = propCompanyId || paramCompanyId || '';
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  // Handle adding a new user from modal
  const handleAddUser = (userId: string) => {
    addUserMutation.mutate(userId);
  };

  // Fetch site users
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['siteUsers', siteId],
    queryFn: () =>
      searchProjectMembers({
        customerId: companyId,
        projectId: siteId,
        pageNumber: 1,
        pageSize: 100, // Adjust as needed
        filterOperator: FilterOperator.And,
        filterFilters: [
          {
            field: 'memberType',
            action: Comparator.NEq,
            value: 'MonitoringCompany',
          },
        ],
      }),
    enabled: !!siteId && !!companyId,
  });

  // Add user mutation
  const addUserMutation = useMutation({
    mutationFn: (userId: string) => postAddProjectMemberCustomer(companyId, siteId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteUsers', siteId] });
      toast({
        title: 'Success',
        description: 'User added to site successfully',
        variant: 'default',
      });
    },
    onError: (error) => {
      console.error('Error adding user to site:', error);
      toast({
        title: 'Error',
        description: 'Failed to add user to site',
        variant: 'destructive',
      });
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => removeProjectMember(companyId, siteId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteUsers', siteId] });
      toast({
        title: 'Success',
        description: 'User removed from site successfully',
        variant: 'default',
      });
    },
    onError: (error) => {
      console.error('Error removing user from site:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove user from site',
        variant: 'destructive',
      });
    },
  });

  // Filter users based on search term
  const filteredUsers = usersData?.rows?.filter(
    (user) =>
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!companyId) {
    return <div>Company ID is required</div>;
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-900/20 p-4 text-red-400">
        Error loading site users. Please try again.
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-auto p-4">
      <div className="min-w-[800px] space-y-4">
        <div className="bg-card1 flex flex-col justify-between space-y-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:space-y-0">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              type="search"
              placeholder="Search users..."
              className="w-full max-w-[500px] bg-[#1a1a1a]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              isSearch
            />
          </div>
          <Button onClick={() => setIsAddUserModalOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>

          <AddUserModal
            isOpen={isAddUserModalOpen}
            onClose={() => setIsAddUserModalOpen(false)}
            onAddUser={handleAddUser}
            existingUserIds={usersData?.rows?.map((user) => user.id || '') || []}
          />
        </div>

        <div className="w-full overflow-hidden rounded-md border">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%] min-w-[150px]">Name</TableHead>
                <TableHead className="w-[55%] min-w-[200px]">Email</TableHead>
                <TableHead className="w-[15%] min-w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers?.length ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="truncate font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell className="truncate">{user.email || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-900/20 hover:text-red-400"
                        onClick={() => user.id && removeUserMutation.mutate(user.id)}
                        disabled={removeUserMutation.isPending}
                      >
                        {removeUserMutation.isPending &&
                        removeUserMutation.variables === user.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        <span className="sr-only">Remove</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default SiteUsers;
